'use client';

import { useState } from 'react';
import { ProviderInfo, ConnectionTestResult } from '../../types/ai-settings';
import { motion } from 'framer-motion';

interface ProviderSelectorProps {
  providers: ProviderInfo[];
  selectedProvider: string;
  onProviderChange: (providerId: string) => void;
  onTestConnection: (providerId: string) => Promise<ConnectionTestResult>;
}

export function ProviderSelector({
  providers,
  selectedProvider,
  onProviderChange,
  onTestConnection,
}: ProviderSelectorProps) {
  const [testingProvider, setTestingProvider] = useState<string | null>(null);

  const handleTestConnection = async (providerId: string) => {
    setTestingProvider(providerId);
    try {
      const result = await onTestConnection(providerId);
      if (!result.success) {
        console.error('Connection test failed:', result.error);
      }
    } finally {
      setTestingProvider(null);
    }
  };

  return (
    <div className="bg-white/5 rounded-lg p-4">
      <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
        <span>🏢</span>
        AI Provider
      </h3>
      
      <div className="grid gap-3">
        {providers.map((provider) => {
          const isSelected = provider.id === selectedProvider;
          const isTesting = testingProvider === provider.id;
          
          return (
            <motion.div
              key={provider.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`relative rounded-lg border transition-all cursor-pointer ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-500/10'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              }`}
              onClick={() => onProviderChange(provider.id)}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{provider.icon}</span>
                    <div>
                      <h4 className="text-sm font-medium text-white">
                        {provider.name}
                      </h4>
                      <p className="text-xs text-white/60 mt-1">
                        {provider.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <a
                          href={provider.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                          Visit website →
                        </a>
                        {provider.models.length > 0 && (
                          <span className="text-xs text-white/40">
                            {provider.models.length} models available
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Connection Status */}
                    <div className="flex items-center gap-2">
                      {provider.status === 'connected' && (
                        <span className="flex items-center gap-1 text-xs text-green-400">
                          <span className="w-2 h-2 bg-green-400 rounded-full" />
                          Connected
                        </span>
                      )}
                      {provider.status === 'disconnected' && provider.requiresApiKey && (
                        <span className="flex items-center gap-1 text-xs text-yellow-400">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full" />
                          API key required
                        </span>
                      )}
                      {provider.status === 'error' && (
                        <span className="flex items-center gap-1 text-xs text-red-400">
                          <span className="w-2 h-2 bg-red-400 rounded-full" />
                          Error
                        </span>
                      )}
                      {provider.status === 'checking' && (
                        <span className="flex items-center gap-1 text-xs text-blue-400">
                          <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                          Checking...
                        </span>
                      )}
                    </div>
                    
                    {/* Test Connection Button */}
                    {provider.requiresApiKey && provider.status !== 'disconnected' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestConnection(provider.id);
                        }}
                        disabled={isTesting}
                        className="px-2 py-1 text-xs bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white/80 rounded transition-colors"
                      >
                        {isTesting ? 'Testing...' : 'Test'}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Error Message */}
                {provider.error && (
                  <div className="mt-2 text-xs text-red-400 bg-red-400/10 rounded p-2">
                    {provider.error}
                  </div>
                )}
              </div>
              
              {/* Selected Indicator */}
              {isSelected && (
                <div className="absolute inset-0 rounded-lg border-2 border-indigo-500 pointer-events-none" />
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}